import React, { useMemo } from "react";
import { Chart, TimeScale } from "chart.js";
import * as Chartjs from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import dayjs from "dayjs";
import {
  getRandomBackgroundColor,
  getXLabelOptions,
  to3DayAverage,
  toMonthlyAverage,
  toWeeklyAverage,
} from "../../utils/helpers";
import "chartjs-adapter-dayjs-4/dist/chartjs-adapter-dayjs-4.esm";

const controllers = Object.values(Chartjs).filter(
  (chart) => chart.id !== undefined
);

const skipped = (ctx, value) =>
  ctx.p0.skip || ctx.p1.skip ? value : undefined;

Chart.register(...controllers, TimeScale);

const CurveChart = ({
  allDates,
  selectedBodyParts,
  fromPdf = false,
  chartRef,
  aggregationView = "default",
}) => {
  const bodyPartColorMap = {};
  const getBodyPartColor = (bodyPart) => {
    if (!bodyPartColorMap[bodyPart]) {
      const index = Object.keys(bodyPartColorMap).length;
      bodyPartColorMap[bodyPart] = getRandomBackgroundColor(index);
    }

    return bodyPartColorMap[bodyPart];
  };

  const labelsChartData = useMemo(() => {
    switch (aggregationView) {
      case "default":
        return allDates?.map((data) => dayjs(data?.date).format("MM/DD"));
      case "monthly":
        return allDates?.map((data) => dayjs(data?.date).format("MM/DD/YYYY"));
      case "weekly":
        return allDates?.map((data) => dayjs(data?.date).format("MM/DD/YYYY"));
      case "days":
        return allDates?.map((data) => dayjs(data?.date).format("MM/DD/YYYY"));

      case "12h":
      case "1h":
      case "3h":
      case "6h":
        return allDates?.map((data) => {
          const date = dayjs(data?.date).format("MM/DD/YYYY");
          const timeEntry = data?.body_parts_data?.find((item) => item?.time);
          const time = timeEntry ? dayjs(timeEntry.time).format("hh:mm A") : "";
          return time ? `${date} ${time}` : date;
        });
      // return allDates?.flatMap((data) => {
      //   const date = dayjs(data?.date).format("MM/DD/YYYY");

      //   // Map through all items in body_parts_data and extract times
      //   const timeEntries = data?.body_parts_data
      //     ?.map((item) =>
      //       item?.time ? dayjs(item.time).format("hh:mm A") : null
      //     )
      //     .filter(Boolean); // Remove null values

      //   // Return each date-time combination
      //   return timeEntries.length > 0
      //     ? timeEntries.map((time) => `${date} ${time}`)
      //     : [date];
      // });

      default:
        return allDates?.map((data) => dayjs(data?.date).format("MM/DD"));
    }
  }, [allDates, aggregationView]);

  const individualBodyPartPain = allDates?.map((item) => ({
    bodyParts: item?.body_parts_data || [],
    date: item?.date,
  }));

  const dates_from_pain_entries = allDates?.map((data) =>
    dayjs(data?.date).format("YYYY-MM-DD")
  );

  const no_medicine = allDates?.map((date) => {
    const sleepLossEntry = date?.body_parts_data?.find((entry) =>
      entry?.independent_of_pain_entry === true
        ? entry?.medicine_adherence === "no"
        : entry?.medicine_entry?.medicine_adherence === "no"
    );
    return sleepLossEntry ? 10 : null;
  });

  const not_prescribed_medicine = allDates?.map((date) => {
    const sleepLossEntry = date?.body_parts_data?.find((entry) =>
      entry?.independent_of_pain_entry === true
        ? entry?.medicine_adherence === "na"
        : entry?.medicine_entry?.medicine_adherence === "na"
    );
    return sleepLossEntry ? 10 : null;
  });

  const data = {
    labels: labelsChartData,
    datasets: [
      {
        type: "bar",
        label: "Medication missed",
        backgroundColor: "rgba(255, 32, 32, 0.4)",
        barThickness: 4,
        data: no_medicine,
      },
      {
        type: "bar",
        label: "Medication not prescribed",
        backgroundColor: "#FEEBB1",
        barThickness: 4,
        data: not_prescribed_medicine,
      },
      ...selectedBodyParts?.map((bodyPart, index) => {
        const backgroundColor = getBodyPartColor(index);
        const pain_values = [];
        for (const pain_entry_date of dates_from_pain_entries) {
          let found = false;
          for (const individual_pain_entry of individualBodyPartPain) {
            for (const individual_body_part of individual_pain_entry?.bodyParts ||
              []) {
              if (
                `${individual_body_part?.side?.toLowerCase()} ${individual_body_part?.name?.toLowerCase()}` ===
                  bodyPart?.toLowerCase() &&
                individual_pain_entry?.date === pain_entry_date
              ) {
                pain_values.push(individual_body_part?.pain_level);
                found = true;
              }
            }
          }

          if (!found) {
            pain_values.push(null);
          }
        }

        return {
          data: pain_values,
          backgroundColor,
          borderColor: backgroundColor,
          label: bodyPart.charAt(0).toUpperCase() + bodyPart.slice(1),
          spanGaps: true,
          pointStyle: "circle",
          pointRadius: 2,
          borderWidth: 1,
          barThickness: 10,
          segment: {
            borderColor: (ctx) => skipped(ctx, backgroundColor),
            borderDash: (ctx) => skipped(ctx, [6, 6]),
          },
        };
      }),
    ],
  };

  // DATA WITHOUT MEDICATION BARS AND MONHTLY/WEEKLY VIEW
  const finalChartData = useMemo(() => {
    switch (aggregationView) {
      case "monthly":
        return toMonthlyAverage(data);

      case "weekly":
        return toWeeklyAverage(data);

      case "days":
        return to3DayAverage(data);

      case "12h":
      case "1h":
      case "3h":
      case "6h":
        return {
          ...data,
          datasets: data.datasets.filter(
            (dataset) =>
              dataset.label !== "Medication missed" &&
              dataset.label !== "Medication not prescribed"
          ),
        };

      default:
        return data;
    }
  }, [data, aggregationView]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    skipNull: true,
    scales: {
      x: getXLabelOptions(aggregationView, labelsChartData),
      y: {
        title: {
          display: true,
          text: "Pain Level",
        },
        beginAtZero: true,
        max: 11,
        ticks: {
          callback: (value, index, values) => {
            return value === 11 ? null : value;
          },
          stepSize: 2,
        },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          title: () => {
            if (
              aggregationView === "12h" ||
              aggregationView === "1h" ||
              aggregationView === "3h" ||
              aggregationView === "6h"
            ) {
              return "";
            }
          },
          label: (tooltipItem) => {
            const value = tooltipItem.raw;
            return value !== null && value !== undefined
              ? `${tooltipItem.dataset.label}: ${value}`
              : "";
          },
        },
      },
      customCanvasBackgroundColor: {
        color: "#fff",
      },
      legend: {
        position: "bottom",
        labels: {
          filter: (legendItem, chartData) => {
            const dataset = chartData.datasets[legendItem.datasetIndex];
            return dataset.data.some(
              (value) => value !== null && value !== undefined
            );
          },
          boxWidth: 15,
          boxHeight: 10,
          usePointStyle: true,
          pointStyle: "rectRounded",
          borderRadius: 11,
          color: "rgba(0, 0, 0, 0.38)",
          font: {
            size: 12,
          },
        },
        align: "end",
      },
    },
  };

  return (
    <>
      {aggregationView === "12h" ||
      aggregationView === "1h" ||
      aggregationView === "3h" ||
      aggregationView === "6h" ? (
        <Bar
          ref={chartRef}
          data={finalChartData}
          options={options}
          style={{ width: "100% !important" }}
        />
      ) : (
        <Line
          ref={chartRef}
          data={finalChartData}
          options={options}
          style={{ width: "100% !important" }}
        />
      )}
    </>
  );
};

export default CurveChart;
